import React, { useCallback, useEffect, useMemo, useState } from "react";
import StarRating from "../../../shared/components/StarRating";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import DeletionCaseOutcomeSummary from "../../../shared/components/deletionCase/DeletionCaseOutcomeSummary";
import {
  REVIEW_OUTCOME,
  applyBulkReviewOutcome,
  getDraftReviewOutcome,
  normalizeReviewOutcome,
  outcomeBadgeClass,
} from "../../../shared/utils/deletionCaseOutcomes";
import {
  formatReviewDateDe,
  normalizeDeletionCaseReviewItems,
  platformLabel,
  resolveReviewPlatformUrl,
} from "../../../shared/utils/deletionCaseReviews";
import { getReviewOutcomeOptions, useTranslation } from "../../../i18n";
const PREVIEW_WORD_LIMIT = 18;

function truncateWords(text, maxWords = PREVIEW_WORD_LIMIT) {
  const trimmed = (text || "").trim();
  if (!trimmed) return { preview: "", isTruncated: false };
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) {
    return { preview: trimmed, isTruncated: false };
  }
  return {
    preview: `${words.slice(0, maxWords).join(" ")}…`,
    isTruncated: true,
  };
}

function AdminReviewCard({
  review,
  currentOutcome,
  outcomeOptions,
  saving,
  onOutcomeChange,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const text = review.text || "";
  const { preview, isTruncated } = truncateWords(text);
  const showToggle = isTruncated || expanded;
  const platformName = platformLabel(review.platform);
  const listingUrl = resolveReviewPlatformUrl(review);

  return (
    <article className="flex h-full min-w-0 max-w-full flex-col overflow-hidden rounded-xl border border-outline-variant/30 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <StarRating rating={review.rating} size="md" />
            <span className="truncate font-headline-md text-headline-md font-bold text-primary">
              {review.author}
            </span>
          </div>
          {review.createTime ? (
            <time
              className="mt-1 block text-label-sm text-on-surface-variant"
              dateTime={review.createTime}
            >
              {formatReviewDateDe(review.createTime)}
            </time>
          ) : null}
          {review.locationName || listingUrl ? (
            <div className="mt-2 space-y-1">
              {listingUrl ? (
                <a
                  href={listingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-1.5 text-label-sm font-semibold text-secondary hover:underline"
                  title={listingUrl}
                >
                  <MaterialIcon name="open_in_new" className="shrink-0 text-[16px]" />
                  <span className="truncate">
                    {review.locationName ||
                      t("adminOutcome.openPlatform", { platform: platformName })}
                  </span>
                </a>
              ) : review.locationName ? (
                <p className="truncate text-label-sm text-on-surface-variant">
                  {review.locationName}
                </p>
              ) : null}
              {listingUrl ? (
                <p className="break-all text-[11px] leading-snug text-outline">
                  {listingUrl}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="shrink-0">
          <EnterpriseFilterSelect
            value={currentOutcome}
            onChange={(value) => onOutcomeChange(review.id, value)}
            options={outcomeOptions}
            disabled={saving}
            minWidth="148px"
            fullWidth={false}
            usePortal
            preferPlacement="auto"
            align="right"
            stopPropagation
            ariaLabel={t("adminOutcome.statusFor", { author: review.author })}
            buttonClassName={`flex h-9 min-w-[132px] items-center justify-between gap-1 rounded-full border-0 px-3 text-[12px] font-semibold ring-1 ring-inset transition-all hover:ring-secondary/40 focus:outline-none focus:ring-2 focus:ring-secondary disabled:opacity-60 ${outcomeBadgeClass(currentOutcome)}`}
          />
        </div>
      </div>

      <div className="mt-3 flex-1">
        {text ? (
          <>
            <p className="break-words text-body-md italic leading-relaxed text-on-surface-variant [overflow-wrap:anywhere]">
              &ldquo;{expanded ? text : preview}&rdquo;
            </p>
            {showToggle ? (
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="mt-1.5 text-label-sm font-semibold text-secondary hover:underline"
              >
                {expanded ? t("reviewHub.seeLess") : t("reviewHub.seeMore")}
              </button>
            ) : null}
          </>
        ) : (
          <p className="text-body-md italic text-on-surface-variant/60">
            {t("deletionCaseUi.noReviewText")}
          </p>
        )}
      </div>
    </article>
  );
}
/**
 * Admin expanded case: single-column review list with dates, truncated text, and inline outcomes.
 */
const AdminDeletionCaseReviewList = ({
  caseData,
  onSaveOutcomes,
  saving = false,
}) => {
  const { t } = useTranslation();
  const outcomeOptions = useMemo(() => getReviewOutcomeOptions(t), [t]);
  const [draft, setDraft] = useState({});

  const items = useMemo(
    () => normalizeDeletionCaseReviewItems(caseData?.items),
    [caseData?.items]
  );

  const platformListings = useMemo(() => {
    const seen = new Set();
    const listings = [];
    items.forEach((item) => {
      const url = resolveReviewPlatformUrl(item);
      if (!url) return;
      const key = `${item.platform || "google"}|${url}`;
      if (seen.has(key)) return;
      seen.add(key);
      listings.push({
        platform: item.platform || "google",
        url,
        name: item.locationName || platformLabel(item.platform),
      });
    });
    return listings;
  }, [items]);

  const savedOutcomesKey = useMemo(
    () =>
      items
        .map((item) => `${item.id}:${normalizeReviewOutcome(item.deletionOutcome)}`)
        .join("|"),
    [items]
  );

  useEffect(() => {
    const next = {};
    items.forEach((item) => {
      next[item.id] = normalizeReviewOutcome(item.deletionOutcome);
    });
    setDraft(next);
  }, [caseData?.id, caseData?.updatedAt, savedOutcomesKey, items]);

  const setOutcome = useCallback((itemId, outcome) => {
    setDraft((prev) => ({ ...prev, [itemId]: outcome }));
  }, []);

  const markAll = useCallback(
    (outcome) => {
      setDraft((prev) => applyBulkReviewOutcome(prev, items, outcome));
    },
    [items]
  );

  const pendingDraftCount = items.filter(
    (item) => getDraftReviewOutcome(draft, item.id) === REVIEW_OUTCOME.PENDING
  ).length;

  const hasChanges = items.some(
    (item) =>
      getDraftReviewOutcome(draft, item.id) !==
      normalizeReviewOutcome(item.deletionOutcome)
  );

  const handleSave = async () => {
    const itemOutcomes = Object.entries(draft).map(([id, deletion_outcome]) => ({
      id: Number(id),
      deletion_outcome,
    }));
    if (!onSaveOutcomes) return;
    await onSaveOutcomes(caseData.id, itemOutcomes);
  };

  const draftSummary = {
    total: items.length,
    deleted: items.filter(
      (i) => getDraftReviewOutcome(draft, i.id) === REVIEW_OUTCOME.DELETED
    ).length,
    notDeleted: items.filter(
      (i) => getDraftReviewOutcome(draft, i.id) === REVIEW_OUTCOME.NOT_DELETED
    ).length,
    pending: pendingDraftCount,
    isPartial: false,
  };
  draftSummary.resolved = draftSummary.deleted + draftSummary.notDeleted;
  draftSummary.allResolved = draftSummary.pending === 0;

  if (!items.length) {
    return (
      <p className="rounded-xl border border-dashed border-outline-variant/40 bg-white px-4 py-8 text-center text-body-md text-on-surface-variant">
        {t("deletionCaseUi.noLinkedReviews")}
      </p>
    );
  }

  return (
    <section
      className="flex max-h-[min(70vh,560px)] w-full min-w-0 max-w-full flex-col overflow-hidden rounded-xl border border-outline-variant/30 bg-white"
      aria-label={t("adminTable.submittedReviews", { count: items.length })}
    >
      <div className="shrink-0 border-b border-outline-variant/20 bg-white px-4 py-3 md:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MaterialIcon
              name="format_list_bulleted"
              className="text-[20px] text-secondary"
            />
            <h3 className="font-headline-md text-headline-md text-primary">
              {t("adminTable.submittedReviews", { count: items.length })}
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={saving || pendingDraftCount === 0}
              onClick={() => markAll(REVIEW_OUTCOME.DELETED)}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-label-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
            >
              {t("adminOutcome.markAllDeleted")}
            </button>
            <button
              type="button"
              disabled={saving || pendingDraftCount === 0}
              onClick={() => markAll(REVIEW_OUTCOME.NOT_DELETED)}
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-label-sm font-semibold text-red-800 transition hover:bg-red-100 disabled:opacity-50"
            >
              {t("adminOutcome.markAllNotDeleted")}
            </button>
          </div>
        </div>
      </div>

      {platformListings.length ? (
        <div className="shrink-0 border-b border-outline-variant/15 bg-surface-container-low/40 px-4 py-3 md:px-5">
          <p className="mb-2 text-label-sm font-semibold uppercase tracking-wide text-on-surface-variant">
            {t("adminOutcome.platformListings")}
          </p>
          <ul className="flex flex-wrap gap-2">
            {platformListings.map((listing) => (
              <li key={`${listing.platform}-${listing.url}`}>
                <a
                  href={listing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-outline-variant/40 bg-white px-3 py-1.5 text-label-sm font-semibold text-secondary transition hover:border-secondary/40 hover:bg-secondary/5"
                >
                  <MaterialIcon name="open_in_new" className="shrink-0 text-[16px]" />
                  <span className="truncate">{listing.name}</span>
                  <span className="shrink-0 text-on-surface-variant">
                    ({platformLabel(listing.platform)})
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {caseData?.outcomeSummary ? (
        <div className="shrink-0 border-b border-outline-variant/15 bg-surface-container-low/50 px-4 py-2 md:px-5">
          <DeletionCaseOutcomeSummary summary={caseData.outcomeSummary} compact />
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <ul className="grid min-w-0 grid-cols-1 gap-4 p-4 md:p-5">
          {items.map((review, index) => (
            <li key={review.id ?? `${review.reviewId}-${index}`} className="min-w-0">
              <AdminReviewCard
                review={review}
                currentOutcome={getDraftReviewOutcome(draft, review.id)}
                outcomeOptions={outcomeOptions}
                saving={saving}
                onOutcomeChange={setOutcome}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-outline-variant/20 bg-white px-4 py-3 md:px-5">
        <p className="text-label-sm text-on-surface-variant">
          {t("adminOutcome.preview")}{" "}
          <DeletionCaseOutcomeSummary summary={draftSummary} compact />
        </p>
        <button
          type="button"
          disabled={saving || !hasChanges}
          onClick={handleSave}
          className="rounded-lg bg-secondary px-4 py-2 text-label-sm font-semibold text-on-secondary transition hover:opacity-90 disabled:opacity-40"
        >
          {saving ? t("common.saving") : t("adminOutcome.saveStatus")}
        </button>
      </div>
    </section>
  );
};

export default AdminDeletionCaseReviewList;
